<template>
  <div>
    <server-error :error="error"/>
    <div>
      <div class="mt-2 mb-3">
        <button
          class="b-btn b-btn_type_primary"
          type="button"
          :data-cy="`export-payment-liabilities-total`"
          @click="exportPayments"
        >{{ $t('CONTENT.export') }}
        </button>
      </div>
      <div class="b-payment-widget__table" v-if="paymentChartData">
        <div class="b-payment-widget__columns-group-wrapper">
          <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
            {{ $t('CONTENT.date') }}
          </div>
          <div class="b-reports__table-header-block-wrap">
            <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
              {{ $t('CONTENT.charts.asset_cost_on_startDate') }}
            </div>
          </div>
          <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell"
               v-if="paymentChartData.assetCostGroup.adjustments">
            {{ $t('CONTENT.charts.adjustments') }}
          </div>
          <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
            {{ $t('CONTENT.events.initialRecognition') }}
          </div>
          <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
            {{ $t('CONTENT.charts.amortization') }}
          </div>
          <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell"
               v-if="paymentChartData.assetCostGroup.devaluations">
            {{ $t('CONTENT.reportPage.report_devaluation') }}
          </div>
          <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
            {{ $t('CONTENT.charts.asset_cost_on_endDate') }}
          </div>
          <div class="b-reports__table-header-block-wrap" v-for="item in paymentChartData.liabilityGroups">
            <div class="b-full-table__cell b-full-table__header-cell b-payment-widget__columns-group-name-title">
              {{ getFullCurrency(item.currencyRateType) }}
            </div>
            <div class="b-reports__table-header-block-inner">
              <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
                {{ $t('CONTENT.charts.liability_on_startDate') }}
              </div>
              <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
                {{ $t('CONTENT.events.initialRecognition') }}
              </div>
              <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell"
                   v-if="item.adjustments !== null">
                {{ $t('CONTENT.charts.adjustments') }}
              </div>
              <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
                {{ $t('CONTENT.charts.percentageLosses') }}
              </div>
              <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
                {{ $t('CONTENT.charts.result') }}
              </div>
              <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
                {{ $t('CONTENT.charts.discount_result') }}
              </div>
              <div class="b-full-table__cell b-full-table__header-cell b-reports__table-header-cell">
                {{ $t('CONTENT.charts.liability_on_endDate') }}
              </div>
            </div>
          </div>
        </div>
        <div class="b-payment-widget__columns-content-wrap">
          <div class="b-payment-widget__column">
            <div
              class="b-full-table__cell b-payment-widget__column-cell"
              v-for="date in convertedDates"
              :key="date"
            >{{ date }}
            </div>
          </div>
          <div class="b-reports__table-block-wrap">
            <div class="b-payment-widget__column">
              <div
                class="b-full-table__cell b-payment-widget__column-cell"
                v-for="(item, index) in paymentChartData.assetCostGroup.startCosts"
                :key="index"
              >{{ convertDecimalToStringForChart(item) }}
              </div>
            </div>
            <div class="b-payment-widget__column" v-if="paymentChartData.assetCostGroup.adjustments">
              <div
                class="b-full-table__cell b-payment-widget__column-cell"
                v-for="(item, index) in paymentChartData.assetCostGroup.adjustments"
                :key="index"
              >{{ convertDecimalToStringForChart(item) }}
              </div>
            </div>
            <div class="b-payment-widget__column">
              <div
                class="b-full-table__cell b-payment-widget__column-cell"
                v-for="(item, index) in paymentChartData.assetCostGroup.initialRecognition"
                :key="index"
              >{{ convertDecimalToStringForChart(item) }}
              </div>
            </div>
            <div class="b-payment-widget__column">
              <div
                class="b-full-table__cell b-payment-widget__column-cell"
                v-for="(item, index) in paymentChartData.assetCostGroup.amortizations"
                :key="index"
              >{{ convertDecimalToStringForChart(item) }}
              </div>
            </div>
            <div class="b-payment-widget__column" v-if="paymentChartData.assetCostGroup.devaluations">
              <div
                class="b-full-table__cell b-payment-widget__column-cell"
                v-for="(item, index) in paymentChartData.assetCostGroup.devaluations"
                :key="index"
              >{{ convertDecimalToStringForChart(item) }}
              </div>
            </div>
            <div class="b-payment-widget__column">
              <div
                class="b-full-table__cell b-payment-widget__column-cell"
                v-for="(item, index) in paymentChartData.assetCostGroup.endCosts"
                :key="index"
              >{{ convertDecimalToStringForChart(item) }}
              </div>
            </div>
          </div>
          <div v-for="item in paymentChartData.liabilityGroups" class="b-reports__table-block-wrap">
            <div class="b-payment-widget__column">
              <div class="b-full-table__cell b-payment-widget__column-cell" v-for="(cost, index) in item.startCosts">
                {{ convertDecimalToStringForChart(cost) }}
              </div>
            </div>
            <div class="b-payment-widget__column">
              <div class="b-full-table__cell b-payment-widget__column-cell"
                   v-for="(cost, index) in item.initialRecognition">
                {{ convertDecimalToStringForChart(cost) }}
              </div>
            </div>
            <div class="b-payment-widget__column" v-if="item.adjustments !== null">
              <div class="b-full-table__cell b-payment-widget__column-cell" v-for="(cost, index) in item.adjustments">
                {{ convertDecimalToStringForChart(cost) }}
              </div>
            </div>
            <div class="b-payment-widget__column">
              <div class="b-full-table__cell b-payment-widget__column-cell"
                   v-for="(cost, index) in item.percentageLosses">
                {{ convertDecimalToStringForChart(cost) }}
              </div>
            </div>
            <div class="b-payment-widget__column">
              <div class="b-full-table__cell b-payment-widget__column-cell" v-for="(cost, index) in item.datesCosts">
                {{ convertDecimalToStringForChart(cost) }}
              </div>
            </div>
            <div class="b-payment-widget__column">
              <div class="b-full-table__cell b-payment-widget__column-cell"
                   v-for="(cost, index) in item.discountedDatesCosts">
                {{ convertDecimalToStringForChart(cost) }}
              </div>
            </div>
            <div class="b-payment-widget__column">
              <div class="b-full-table__cell b-payment-widget__column-cell" v-for="(cost, index) in item.endCosts">
                {{ convertDecimalToStringForChart(cost) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PaymentsCalculationsAPI from "../../api/PaymentsCalculationsAPI";
import {mapGetters} from 'vuex';
import dateConverter from "@/utils/converters/date.converter";
import convertDecimalForChart from "@/utils/converters/convert.decimal.for.chart";
import lodash from "lodash";
import getFullCurrency from "../../../../mixins/getFullCurrency";

export default {
  props: {
    components: {
      required: true,
      type: Array
    },
    contractId: {
      required: true,
      type: Number | null
    },
    currentVersionId: {
      required: true,
      default: String | null
    },
  },
  data() {
    return {
      error: null,
      paymentChartData: null,
      dataForCalculations: null
    };
  },
  mixins: [getFullCurrency],
  watch: {
    async components() {
      await this.calculate();
    },
    async isEditContract() {
      await this.calculate();
    }
  },
  computed: {
    ...mapGetters(["getContractState"]),
    isEditContract() {
      return this.getContractState;
    },
    convertedDates() {
      const dates = lodash.cloneDeep(this.paymentChartData.dates);
      for (let i = 0; i < dates.length; i++) {
        const element = dates[i];
        dates[i] = this.$d(dateConverter.convertToDate(element));
      }
      this.date = dates[0];
      return dates;
    }
  },
  async created() {
    await this.calculate();
  },

  methods: {
    async calculate() {
      this.$fileLoadingManager.startLoading();
      try {
        this.error = null;
        const paymentAPI = new PaymentsCalculationsAPI();
          this.paymentChartData = await paymentAPI.calculateTotalLiabilities(
            { contractId: this.contractId, components: this.components.map(s=> s.idInDB)}, this.currentVersionId
          );
      } catch (e) {
        this.error = e;
      } finally {
        this.$fileLoadingManager.finishLoading();
      }
    },

    convertDecimalToStringForChart(decimal) {
      return convertDecimalForChart(decimal);
    },

    async exportPayments() {
      this.error = null;
      try {
        const api = new PaymentsCalculationsAPI();
        const response = await api.exportResultOfCalculatePaymentsLiabilities(this.paymentChartData);

        let filename = null;
        let filenameInURI = null;

        let headerContentDisposition = response.headers['content-disposition'].split(';');
        for (let i = 0; i < headerContentDisposition.length; i++) {
          let item = headerContentDisposition[i].split('=');
          if (item[0].trim() === 'filename*') {
            filenameInURI = item[1].split("''");
            filename = decodeURIComponent(filenameInURI[1]);
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
      } catch (e) {
        this.error = e;
        console.error(e);
      }
    }
  }
};
</script>

<style>
</style>
